'use client';

import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { cacheKeys } from '@/shared/lib/cache-invalidation';
import { goalsAutosaveCoordinator } from '@/features/project/lib/goals-autosave-coordinator';

/**
 * Open/close state for the shared project-goals sheet (ProjectGoalsSheet)
 * opened from the estimate's Region / Facade-area rows. On close, await any
 * pending goals/region/area PATCH and re-fetch the estimate: the divergence
 * flags (`has_diverged_construction_region`, `has_diverged_facade_area`) are
 * computed server-side at read time, so an edit must reflect in the inline
 * warnings as soon as the sheet closes.
 *
 * Shared by the two-column inspector (EstimateSettingsPanel) and the stacked
 * editor (EstimateEditor) — identical wiring on both layouts.
 */
export function useEstimateGoalsSheet(
  projectId: string,
  estimateId: string | undefined
) {
  const queryClient = useQueryClient();
  const [showGoalsSheet, setShowGoalsSheet] = useState(false);

  const handleGoalsSheetChange = (open: boolean) => {
    setShowGoalsSheet(open);
    if (!open && estimateId) {
      void goalsAutosaveCoordinator.flushAndAwait(projectId).then(() => {
        void queryClient.invalidateQueries({
          queryKey: cacheKeys.estimates.detail(estimateId),
        });
      });
    }
  };

  return { showGoalsSheet, handleGoalsSheetChange };
}
